import { apiFetch, getAuthToken, setAuthToken } from "./http";
import { getApiPageSize } from "./config";
import { decodeHtmlEntities, excerptFromHtml, stripHtml } from "../utils/text";
import type { BlogCategory, BlogComment, BlogPagination, BlogPost } from "../types/blog";

interface RawPagination {
    page?: number;
    limit?: number;
    total?: number;
    total_pages?: number;
    totalPages?: number;
}

interface RawPost {
    id?: string | number;
    ID?: string | number;
    wp_post_id?: number | null;
    slug?: string;
    post_name?: string;
    title?: string;
    post_title?: string;
    excerpt?: string | null;
    post_excerpt?: string | null;
    content_html?: string | null;
    post_content?: string | null;
    category_id?: string | null;
    category_name?: string | null;
    category?: string | null;
    category_slug?: string | null;
    categorySlug?: string | null;
    featured_image_url?: string | null;
    image?: string | null;
    image_alt?: string | null;
    og_image_url?: string | null;
    ogImageUrl?: string | null;
    status?: string;
    seo_title?: string | null;
    seo_description?: string | null;
    published_at?: string | null;
    publishedAt?: string | null;
    updated_at?: string | null;
    updatedAt?: string | null;
    created_at?: string | null;
    author_id?: string | null;
    authorId?: string | null;
    author?: { id?: string | number; name?: string; slug?: string; avatar_url?: string } | null;
    author_name?: string | null;
    authorName?: string | null;
    tags?: string[] | string;
}

interface RawCategory {
    id?: string | number;
    term_id?: number;
    wp_term_id?: number | null;
    name?: string;
    slug?: string;
    description?: string | null;
    post_count?: number;
    postCount?: number;
    created_at?: string | null;
    updated_at?: string | null;
}

interface RawComment {
    id?: string | number;
    comment_ID?: string | number;
    post_id?: string | number;
    user_id?: string | null;
    parent_id?: string | null;
    author_name?: string;
    comment_author?: string;
    content?: string;
    comment_content?: string;
    status?: string;
    created_at?: string;
    updated_at?: string;
}

interface PostsResponse {
    status?: string;
    posts?: RawPost[];
    pagination?: RawPagination;
}

interface CategoriesResponse {
    status?: string;
    categories?: RawCategory[];
}

interface PostResponse {
    status?: string;
    post?: RawPost;
    relatedPosts?: RawPost[];
    related_posts?: RawPost[];
}

interface CommentsResponse {
    status?: string;
    comments?: RawComment[];
}

interface ImageUploadResponse {
    status?: string;
    image_url?: string;
    url?: string;
}

interface AuthResponse {
    status?: string;
    message?: string;
    user?: GreenUnisonUser;
    token?: string;
    accessToken?: string;
    access_token?: string;
    refreshToken?: string;
    refresh_token?: string;
    requiresTOTPVerification?: boolean;
    requiresEmailVerification?: boolean;
}

export interface GreenUnisonUser {
    id: string;
    first_name?: string;
    last_name?: string;
    name?: string;
    email: string;
    email_verified?: boolean;
    totp_enabled?: boolean;
    email_mfa_enabled?: boolean;
    profile_photo_url?: string | null;
    roles?: string[];
    permissions?: string[];
}

export interface LoginInput {
    email: string;
    password: string;
    rememberMe?: boolean;
}

export interface SignupInput {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    phone_number?: string;
    company_name?: string;
    invitation_token?: string;
}

export interface CreateCommentInput {
    content: string;
    parentId?: string | null;
}

export interface MfaVerifyInput {
    userId: string;
    code: string;
    trustedDevice?: boolean;
    rememberMe?: boolean;
}

export interface CreateBlogPostInput {
    title: string;
    slug?: string;
    contentHtml: string;
    excerpt?: string;
    metaDescription?: string;
    seoTitle?: string;
    categorySlug?: string;
    status?: "draft" | "published";
    ogImageUrl?: string;
    featuredImageUrl?: string;
    publishedAt?: string;
    tags?: string;
    featuredImage?: File | null;
}

export interface UpdateBlogPostInput {
    title?: string;
    slug?: string;
    contentHtml?: string;
    excerpt?: string;
    metaDescription?: string;
    seoTitle?: string;
    categorySlug?: string;
    status?: "draft" | "published";
    ogImageUrl?: string;
    featuredImageUrl?: string;
    publishedAt?: string;
    tags?: string;
    featuredImage?: File | null;
}

function normalizeDate(value?: string | null) {
    if (!value) return undefined;
    return value;
}

function normalizePost(raw: RawPost): BlogPost {
    const title = decodeHtmlEntities(
        String(raw.title ?? raw.post_title ?? "Untitled Green Unison guide"),
    );
    const contentHtml = raw.content_html ?? raw.post_content ?? "";
    const excerptSource = raw.excerpt ?? raw.post_excerpt ?? "";
    const excerpt = stripHtml(excerptSource || "") || excerptFromHtml(contentHtml || "");
    const authorName = raw.authorName ?? raw.author_name ?? raw.author?.name ?? undefined;

    return {
        id: raw.id
            ? String(raw.id)
            : raw.ID
              ? String(raw.ID)
              : raw.wp_post_id
                ? String(raw.wp_post_id)
                : undefined,
        wpPostId: typeof raw.wp_post_id === "number" ? raw.wp_post_id : null,
        slug: String(raw.slug ?? raw.post_name ?? ""),
        title,
        excerpt: decodeHtmlEntities(excerpt),
        contentHtml: contentHtml || undefined,
        category: raw.category_name ?? raw.category ?? undefined,
        categorySlug: raw.category_slug ?? raw.categorySlug ?? undefined,
        categoryId: raw.category_id ?? null,
        image: raw.featured_image_url ?? raw.image ?? undefined,
        imageAlt: raw.image_alt ?? title,
        ogImageUrl: raw.og_image_url ?? raw.ogImageUrl ?? undefined,
        status: raw.status,
        seoTitle: raw.seo_title ?? undefined,
        seoDescription: raw.seo_description ?? undefined,
        publishedAt: normalizeDate(raw.published_at ?? raw.publishedAt),
        updatedAt: normalizeDate(raw.updated_at ?? raw.updatedAt),
        createdAt: normalizeDate(raw.created_at),
        authorId: raw.authorId ?? raw.author_id ?? null,
        authorName,
        author: raw.author
            ? {
                  id: raw.author.id,
                  name: raw.author.name,
                  slug: raw.author.slug,
                  avatarUrl: raw.author.avatar_url,
              }
            : authorName
              ? { name: authorName }
              : undefined,
        tags: Array.isArray(raw.tags)
            ? raw.tags
            : typeof raw.tags === "string" && raw.tags.trim()
              ? raw.tags
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter(Boolean)
              : [],
    };
}

function normalizeCategory(raw: RawCategory): BlogCategory {
    return {
        id: raw.id ? String(raw.id) : raw.term_id ? String(raw.term_id) : undefined,
        wpTermId: raw.wp_term_id ?? raw.term_id ?? null,
        name: decodeHtmlEntities(String(raw.name ?? "Uncategorized")),
        slug: String(raw.slug ?? "uncategorized"),
        description: raw.description ?? null,
        postCount: Number(raw.post_count ?? raw.postCount ?? 0),
        createdAt: raw.created_at ?? null,
        updatedAt: raw.updated_at ?? null,
    };
}

function normalizeComment(raw: RawComment): BlogComment {
    return {
        id: String(raw.id ?? raw.comment_ID ?? ""),
        postId: raw.post_id ? String(raw.post_id) : undefined,
        userId: raw.user_id ?? null,
        parentId: raw.parent_id ?? null,
        authorName: String(raw.author_name ?? raw.comment_author ?? "Green Unison reader"),
        content: String(raw.content ?? raw.comment_content ?? ""),
        status: raw.status,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
    };
}

function normalizePagination(raw?: RawPagination): BlogPagination {
    return {
        page: Number(raw?.page ?? 1),
        limit: Number(raw?.limit ?? getApiPageSize()),
        total: Number(raw?.total ?? 0),
        totalPages: Number(raw?.total_pages ?? raw?.totalPages ?? 0),
    };
}

function getTokenFromAuthResponse(response: AuthResponse) {
    return response.access_token ?? response.accessToken ?? response.token ?? null;
}

export async function getPosts(
    params: {
        page?: number;
        limit?: number;
        category?: string;
        q?: string;
        featured?: boolean;
    } = {},
) {
    const data = await apiFetch<PostsResponse>("/api/blog/posts", {
        query: {
            page: params.page,
            limit: params.limit,
            category: params.category,
            q: params.q,
            featured: params.featured,
        },
    });

    return {
        posts: (data.posts ?? []).map(normalizePost).filter((post) => Boolean(post.slug)),
        pagination: normalizePagination(data.pagination),
    };
}

export async function getAllPostsForBuild() {
    const limit = getApiPageSize();
    const first = await getPosts({ page: 1, limit });
    const posts = [...first.posts];
    const totalPages = first.pagination.totalPages || 1;

    for (let page = 2; page <= totalPages; page += 1) {
        const next = await getPosts({ page, limit });
        posts.push(...next.posts);
    }

    return posts;
}

export async function getCategories() {
    const data = await apiFetch<CategoriesResponse>("/api/blog/categories");

    return (data.categories ?? []).map(normalizeCategory);
}

export async function getPostBySlug(slug: string) {
    const data = await apiFetch<PostResponse>(`/api/blog/posts/${encodeURIComponent(slug)}`);

    return data.post ? normalizePost(data.post) : null;
}

export async function getRelatedPosts(post: BlogPost, allPosts: BlogPost[] = []) {
    return allPosts
        .filter((item) => item.slug !== post.slug)
        .filter((item) => {
            const sharesCategory = Boolean(
                item.categorySlug && item.categorySlug === post.categorySlug,
            );
            const sharesCategoryName = Boolean(item.category && item.category === post.category);
            const sharesTag = item.tags?.some((tag) => post.tags?.includes(tag)) ?? false;
            return sharesCategory || sharesCategoryName || sharesTag;
        })
        .slice(0, 3);
}

export async function getComments(slug: string) {
    const data = await apiFetch<CommentsResponse>(
        `/api/blog/posts/${encodeURIComponent(slug)}/comments`,
    );

    return (data.comments ?? []).map(normalizeComment);
}

export async function postComment(slug: string, input: CreateCommentInput) {
    const data = await apiFetch<{ status?: string; comment?: RawComment }>(
        `/api/blog/posts/${encodeURIComponent(slug)}/comments`,
        {
            method: "POST",
            token: getAuthToken(),
            body: JSON.stringify({
                content: input.content,
                parent_id: input.parentId ?? null,
                parentId: input.parentId ?? null,
            }),
        },
    );

    if (!data.comment) {
        throw new Error("The API did not return the new comment.");
    }

    return normalizeComment(data.comment);
}

export async function login(input: LoginInput) {
    const data = await apiFetch<AuthResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({
            email: input.email,
            password: input.password,
            rememberMe: Boolean(input.rememberMe),
            remember_me: Boolean(input.rememberMe),
        }),
    });
    const token = getTokenFromAuthResponse(data);

    if (token) setAuthToken(token);

    return {
        user: data.user ?? null,
        token,
        requiresTOTPVerification: Boolean(data.requiresTOTPVerification),
        requiresEmailVerification: Boolean(data.requiresEmailVerification),
    };
}

export async function requestEmailMfaCode(userId: string) {
    return apiFetch<{ status?: string; message?: string }>("/api/mfa/email/request-code", {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
    });
}

export async function verifyTotpLogin(input: MfaVerifyInput) {
    const data = await apiFetch<AuthResponse>("/api/mfa/totp/verify", {
        method: "POST",
        body: JSON.stringify({
            user_id: input.userId,
            token: input.code,
            is_trusted_device: Boolean(input.trustedDevice),
            rememberMe: Boolean(input.rememberMe),
            remember_me: Boolean(input.rememberMe),
        }),
    });
    const token = getTokenFromAuthResponse(data);
    if (token) setAuthToken(token);

    return { user: data.user ?? null, token };
}

export async function verifyEmailMfaLogin(input: MfaVerifyInput) {
    const data = await apiFetch<AuthResponse>("/api/mfa/email/verify-code", {
        method: "POST",
        body: JSON.stringify({
            user_id: input.userId,
            code: input.code,
            is_trusted_device: Boolean(input.trustedDevice),
            rememberMe: Boolean(input.rememberMe),
            remember_me: Boolean(input.rememberMe),
        }),
    });
    const token = getTokenFromAuthResponse(data);
    if (token) setAuthToken(token);

    return { user: data.user ?? null, token };
}

export async function signup(input: SignupInput) {
    const path = input.invitation_token
        ? `/api/auth/register/${encodeURIComponent(input.invitation_token)}`
        : "/api/auth/signup";

    const data = await apiFetch<AuthResponse>(path, {
        method: "POST",
        body: JSON.stringify(input),
    });
    const token = getTokenFromAuthResponse(data);

    if (token) setAuthToken(token);

    return { user: data.user ?? null, token };
}

export async function getCurrentUser() {
    const data = await apiFetch<{ status?: string; user?: GreenUnisonUser }>("/api/users/me", {
        token: getAuthToken(),
    });

    return data.user ?? null;
}

export async function logout() {
    try {
        await apiFetch("/api/auth/logout", {
            method: "DELETE",
            token: getAuthToken(),
        });
    } finally {
        setAuthToken(null);
    }
}

export async function forgotPassword(email: string) {
    return apiFetch<{ status?: string; message?: string }>("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
    });
}

export async function resetPassword(token: string, password: string) {
    return apiFetch<{ status?: string; message?: string }>(
        `/api/auth/reset-password/${encodeURIComponent(token)}`,
        {
            method: "POST",
            body: JSON.stringify({ password }),
        },
    );
}

export async function changePassword(currentPassword: string, newPassword: string) {
    return apiFetch<{ status?: string; message?: string }>("/api/users/change-password", {
        method: "POST",
        token: getAuthToken(),
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
}

export async function requestEmailChange(newEmail: string, password: string) {
    return apiFetch<{ status?: string; message?: string }>("/api/users/change-email/request", {
        method: "POST",
        token: getAuthToken(),
        body: JSON.stringify({ new_email: newEmail, password }),
    });
}

export async function confirmEmailChange(code: string) {
    const data = await apiFetch<{ status?: string; message?: string; user?: GreenUnisonUser }>(
        "/api/users/change-email/confirm",
        {
            method: "POST",
            token: getAuthToken(),
            body: JSON.stringify({ code }),
        },
    );

    return data;
}

export async function uploadBlogEditorImage(file: File) {
    const formData = new FormData();
    formData.set("image", file);

    const data = await apiFetch<ImageUploadResponse>("/api/blog/images", {
        method: "POST",
        token: getAuthToken(),
        body: formData,
    });

    const url = data.image_url ?? data.url;
    if (!url) throw new Error("The API did not return the uploaded image URL.");

    return url;
}

export async function createBlogPost(input: CreateBlogPostInput) {
    const formData = new FormData();
    formData.set("title", input.title);
    if (input.slug) formData.set("slug", input.slug);
    formData.set("content_html", input.contentHtml);
    if (input.excerpt) formData.set("excerpt", input.excerpt);
    if (input.metaDescription) formData.set("meta_description", input.metaDescription);
    if (input.seoTitle) formData.set("seo_title", input.seoTitle);
    if (input.categorySlug) formData.set("category_slug", input.categorySlug);
    if (input.status) formData.set("status", input.status);
    if (input.ogImageUrl) formData.set("og_image_url", input.ogImageUrl);
    if (input.featuredImageUrl) formData.set("featured_image_url", input.featuredImageUrl);
    if (input.publishedAt) formData.set("published_at", input.publishedAt);
    if (input.tags) formData.set("tags", input.tags);
    if (input.featuredImage) formData.set("featured_image", input.featuredImage);

    const data = await apiFetch<{ status?: string; message?: string; post?: RawPost }>(
        "/api/blog/posts",
        {
            method: "POST",
            token: getAuthToken(),
            body: formData,
        },
    );

    if (!data.post) throw new Error("The API did not return the new blog post.");

    return normalizePost(data.post);
}

export async function getEditableBlogPost(slug: string) {
    const data = await apiFetch<PostResponse>(`/api/blog/posts/${encodeURIComponent(slug)}/edit`, {
        token: getAuthToken(),
    });

    if (!data.post) {
        throw new Error("The API did not return the blog post.");
    }

    return normalizePost(data.post);
}

export async function updateBlogPost(slug: string, input: UpdateBlogPostInput) {
    const formData = new FormData();

    if (input.title !== undefined) formData.set("title", input.title);
    if (input.slug !== undefined) formData.set("slug", input.slug);
    if (input.contentHtml !== undefined) formData.set("content_html", input.contentHtml);
    if (input.excerpt !== undefined) formData.set("excerpt", input.excerpt);
    if (input.metaDescription !== undefined)
        formData.set("meta_description", input.metaDescription);
    if (input.seoTitle !== undefined) formData.set("seo_title", input.seoTitle);
    if (input.categorySlug !== undefined) formData.set("category_slug", input.categorySlug);
    if (input.status !== undefined) formData.set("status", input.status);
    if (input.ogImageUrl !== undefined) formData.set("og_image_url", input.ogImageUrl);
    if (input.featuredImageUrl !== undefined)
        formData.set("featured_image_url", input.featuredImageUrl);
    if (input.publishedAt !== undefined) formData.set("published_at", input.publishedAt);
    if (input.tags !== undefined) formData.set("tags", input.tags);
    if (input.featuredImage) formData.set("featured_image", input.featuredImage);

    const data = await apiFetch<{ status?: string; message?: string; post?: RawPost }>(
        `/api/blog/posts/${encodeURIComponent(slug)}`,
        {
            method: "PATCH",
            token: getAuthToken(),
            body: formData,
        },
    );

    if (!data.post) throw new Error("The API did not return the updated blog post.");

    return normalizePost(data.post);
}
